import React, {
  forwardRef,
  useCallback,
  useContext,
  createContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Search,
  X,
  Inbox,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  ArrowUp,
  ChevronDown,
  ChevronsUpDown,
  Eye,
  Pencil,
  Trash2,
  RotateCw,
  Download,
  SlidersHorizontal,
  Filter,
  ListRestart,
  AlertTriangle,
} from "lucide-react";
// Same order as customComponents.js, so the bundler can keep one CSS order
import CustomInputField from "../CustomInputFields/CustomInputField";
import CustomDropdowns from "../CustomDropdowns/CustomDropdowns";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import CustomRadioGroup from "../CustomRadioGroup/CustomRadioGroup";
import CustomToggleSwitch from "../CustomToggleSwitch/CustomToggleSwitch";
import CustomButton from "../CustomButton/CustomButton";
import CustomFormField from "../CustomFormField/CustomFormField";
import MultiSelectDropdown from "../MultiSelectDropdown/MultiSelectDropdown";
import "../CustomTheme/customTheme.css";
import "./CustomAdminTable.css";
import { createPortal } from "react-dom";

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const EMPTY_DATA = [];

/**
 * CustomFilterButton — the table's "Filters (n)" button. The table renders it in its toolbar;
 * use it (via useHeaderFilterButton) to put the same button somewhere else, e.g. a page header.
 */
export const CustomFilterButton = forwardRef(function CustomFilterButton(
  { count = 0, label = "Filters", expanded, onClick, standalone = true, className },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={`cat-filterBtn${standalone ? " cat-filterBtnStandalone" : ""}${count > 0 ? " cat-filterBtn-active" : ""}${className ? ` ${className}` : ""}`}
      onClick={onClick}
      aria-haspopup="dialog"
      aria-expanded={expanded}
      title={count > 0 ? `${count} filter(s) applied — click to change` : "Open filters"}
    >
      <SlidersHorizontal size={14} />
      {label}
      {count > 0 && <span className="cat-filterBadge">{count}</span>}
    </button>
  );
});
const DEFAULT_SEARCH_DEBOUNCE_MS = 400;
const DRAWER_FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ROW_HEIGHT_PRESETS = {
  compact: { header: 40, row: 40 },
  default: { header: 48, row: 52 },
  comfortable: { header: 52, row: 60 },
};

// Lets DeleteAction (used inside the `actions` render prop, which is
// invoked by the table but isn't a JSX child of it in the usual sense)
// reach the table's own confirm-dialog state when deleteDialog=true.
// When the table isn't rendering a provider (deleteDialog=false, the
// default), DeleteAction just falls back to window.confirm as before —
// fully backward compatible.
const CatConfirmContext = createContext(null);

/* ==========================================================
   helpers
   ========================================================== */

function getRowKey(row, rowKey, index) {
  if (typeof rowKey === "function") return rowKey(row, index);
  if (typeof rowKey === "string") return row?.[rowKey] ?? index;
  return row?._id ?? row?.id ?? index;
}

// Supports dot-notation for nested fields, e.g. column.key = "user.name"
function getCellValue(row, column) {
  if (!column.key) return undefined;
  return column.key
    .split(".")
    .reduce(
      (acc, part) => (acc === null || acc === undefined ? acc : acc[part]),
      row,
    );
}

// Builds a compact page list with ellipses, e.g. [1, "...", 4, 5, 6, "...", 20]
function buildPageList(current, totalPages) {
  if (totalPages <= 1) return [1];
  const delta = 1;
  const pages = [1];
  const rangeStart = Math.max(2, current - delta);
  const rangeEnd = Math.min(totalPages - 1, current + delta);

  if (rangeStart > 2) pages.push("...");
  for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
  if (rangeEnd < totalPages - 1) pages.push("...");
  pages.push(totalPages);

  return pages;
}

/* ==========================================================
   reusable exports: status badge + icon action buttons
   These are exported alongside the table so any page can use
   them directly in a column's `render` or in the `actions` slot,
   without redefining the same badge/button styles everywhere.
   ========================================================== */

const STATUS_META = {
  active: { label: "Active", cls: "cat-status-active" },
  inactive: { label: "Inactive", cls: "cat-status-inactive" },
  pending: { label: "Pending", cls: "cat-status-pending" },
  approved: { label: "Approved", cls: "cat-status-approved" },
  rejected: { label: "Rejected", cls: "cat-status-rejected" },
};

/**
 * renderStatus(status)
 * Use directly as a column's render function:
 *   { key: "status", header: "Status", render: (v) => renderStatus(v) }
 * Recognizes (case-insensitively): active, inactive, pending,
 * approved, rejected. Anything else falls back to a neutral badge
 * showing the raw value as-is.
 */
export function renderStatus(status) {
  const meta = STATUS_META[String(status).toLowerCase()] || {
    label: status ?? "—",
    cls: "cat-status-default",
  };
  return <span className={`cat-statusBadge ${meta.cls}`}>{meta.label}</span>;
}

function IconActionButton({
  icon,
  tooltip,
  variant = "default",
  onClick,
  confirmMessage,
}) {
  const confirmCtx = useContext(CatConfirmContext);
  const btnRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);

  const handleClick = (e) => {
    e.stopPropagation(); // never trigger a row's onRowClick
    if (!confirmMessage) {
      onClick?.(e);
      return;
    }
    if (confirmCtx?.requestConfirm) {
      confirmCtx.requestConfirm(confirmMessage, () => onClick?.(e));
    } else if (window.confirm(confirmMessage)) {
      onClick?.(e);
    }
  };

  const showTooltip = () => {
    if (!tooltip || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setTooltipPos({ top: rect.top - 6, left: rect.left + rect.width / 2 });
  };
  const hideTooltip = () => setTooltipPos(null);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`cat-iconBtn cat-iconBtn-${variant}`}
        onClick={handleClick}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        aria-label={tooltip}
      >
        {icon}
      </button>
      {tooltip &&
        tooltipPos &&
        createPortal(
          <span
            className="cat-portalTooltip"
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            {tooltip}
          </span>,
          document.body,
        )}
    </>
  );
}

export const ViewAction = ({ onClick, tooltip = "View" }) => (
  <IconActionButton
    icon={<Eye size={15} />}
    tooltip={tooltip}
    variant="view"
    onClick={onClick}
  />
);

export const EditAction = ({ onClick, tooltip = "Edit" }) => (
  <IconActionButton
    icon={<Pencil size={15} />}
    tooltip={tooltip}
    variant="edit"
    onClick={onClick}
  />
);

export const DeleteAction = ({
  onClick,
  tooltip = "Delete",
  confirmMessage = "Are you sure you want to delete this? This can't be undone.",
}) => (
  <IconActionButton
    icon={<Trash2 size={15} />}
    tooltip={tooltip}
    variant="delete"
    onClick={onClick}
    confirmMessage={confirmMessage}
  />
);

/**
 * RowActions — convenience wrapper for the common View/Edit/Delete
 * trio. Pass only the handlers you need; the rest are simply omitted.
 *   actions={(row) => (
 *     <RowActions onView={() => ...} onEdit={() => ...} onDelete={() => ...} />
 *   )}
 */
export const RowActions = ({
  onView,
  onEdit,
  onDelete,
  deleteConfirmMessage,
}) => (
  <>
    {onView && <ViewAction onClick={onView} />}
    {onEdit && <EditAction onClick={onEdit} />}
    {onDelete && (
      <DeleteAction onClick={onDelete} confirmMessage={deleteConfirmMessage} />
    )}
  </>
);

/**
 * CustomAdminTable
 * ----------------
 * A single reusable table for every admin page. Fully controlled for
 * data/search/sort/pagination — this component never fetches or owns
 * that state itself, it just renders what it's given and calls back
 * on interaction. (Row *selection* is the one exception: it can be
 * either controlled or left fully internal — see enableSelection below.)
 *
 * Layout: header, scrollable body, and pagination footer are three
 * flex children of a wrapper capped at `maxHeight`: with few rows the
 * table is only as tall as its content (no white space), and once the
 * rows reach maxHeight only the body (`.cat-scroll`) scrolls — done with
 * `flex:1 1 auto; min-height:0; overflow:auto`, not JS scroll syncing.
 *
 * ---- Every prop from the previous version still works exactly the
 * same. Everything below the divider is new and optional. ----
 *
 * columns: Array<{
 *   key, header, width?, minWidth?, align?, sortable?, wrap?,
 *   render?: (value, row, rowIndex) => ReactNode,
 *   className?, headerClassName?,
 *   hidden?: boolean,        // NEW — skip rendering this column entirely
 *   group?: string,          // NEW — neighbouring columns with the same group get one
 *                            //       merged header cell above them (2-row header)
 * }>
 * data, loading, rowKey, onRowClick
 * title, search: { value, onChange, placeholder, debounce? }, filters, toolbarActions
 * actions, actionsHeader, stickyActions
 * sort, onSortChange
 * pagination: { page, pageSize, total, onPageChange, onPageSizeChange, pageSizeOptions }
 * emptyTitle, emptyDescription, emptyIcon, emptyState
 * maxHeight, striped, hoverable, dense, className, style, id
 *
 * ---- NEW ----
 * enableSerialNumber?: boolean       adds a leading "#" column computed
 *                                     as (page-1)*pageSize + index + 1
 *                                     (falls back to index+1 with no
 *                                     pagination prop). Total rows — row._total,
 *                                     or rowClassName "cat-rowTotal" /
 *                                     "cat-rowGrandTotal" — get no number and
 *                                     are not counted.
 * serialHeader?: string              header label for that column (default "#")
 * isSerialSkipped?: (row) => boolean override which rows get no serial number
 *
 * enableSelection?: boolean          adds a checkbox column
 * selectedRowKeys?: Array            controlled selection (optional —
 *                                     omit to let the table manage it
 *                                     internally)
 * onSelectionChange?: (keys, rows) => void
 *                                     called on every selection change;
 *                                     `rows` is only the CURRENT PAGE's
 *                                     selected row objects — persisting
 *                                     selection across pages is on the
 *                                     parent if selectedRowKeys is used
 *
 * rowClassName?: (row, index) => string
 *                                     extra class for a body row, e.g.
 *                                     "cat-rowTotal" for bold subtotal rows
 *
 * stickyHeader?: boolean             (default true) set false to let the
 *                                     header scroll away with the body
 *                                     instead of staying pinned
 *
 * rowHeight?: "compact" | "default" | "comfortable"   (default "default")
 *                                     controls actual pixel height of
 *                                     header/rows (40 / 48-52 / 52-60px)
 * compact?: boolean                  shorthand for rowHeight="compact"
 *                                     when rowHeight isn't explicitly set —
 *                                     independent of the existing `dense`
 *                                     prop, which only shrinks font-size
 *
 * enableRefresh?: boolean            shows a refresh icon button in the toolbar
 * onRefresh?: () => void             called when it's clicked; the icon
 *                                     spins automatically while `loading` is true
 *
 * loadingText?: string               optional label shown next to the
 *                                     spinner while loading (skeleton or
 *                                     refetch-overlay); omit for icon-only
 *
 * deleteDialog?: boolean             (default false) when true, DeleteAction/
 *                                     RowActions show a proper in-app
 *                                     confirmation modal instead of the
 *                                     browser's native window.confirm
 *
 * exportButton?: boolean             shows a built-in "Export" button in
 *                                     the toolbar (appears before any
 *                                     custom toolbarActions)
 * onExport?: () => void              called when Export is clicked
 *
 * ---- advanced filter drawer ----
 * The existing `filters` prop (a single quick-filter dropdown in the
 * toolbar) is untouched — this is a SEPARATE, additional mechanism for
 * when you have many filter fields (date ranges, multiple dropdowns,
 * etc.) that don't fit in the toolbar. It opens a side drawer instead.
 *
 * advancedFilters?: Array<{        one entry per field, shown in order
 *   key: string,                     key in the values object
 *   label: string,
 *   type?: "text" | "number" | "date" | "dropdown" | "select" | "toggle" | "radio" | "multiSelect" (value = array)
 *                                     "dropdown" → searchable, value = the WHOLE selected item
 *                                     "select"   → value = the option's id as a string (legacy)
 *   options?: array | (values) => array
 *   defaultValue?: any | () => any    initial value AND the Reset value (default "" / false)
 *   placeholder?, hint?: string | (values) => string
 *   isRequired?: boolean             red * and "<label> is required" on Apply when empty
 *   validate?: (value, values) => string   error message ("" = ok); blocks Apply
 *   hidden?: boolean | (values) => boolean    e.g. Sub Exam only when it has options
 *   disabled?: boolean | (values) => boolean  e.g. lock Payment Type for some roles
 *   clears?: string[]                keys reset when this field changes
 *                                     (Exam → clears ["subExam", "examStream", "batch"])
 *   half?: boolean                   half width: two half fields share one row
 *   minDate?, maxDate?: string | (values) => string   (type "date")
 *   search?, clearable?, getOptionLabel?, getOptionKey?   (type "dropdown")
 *   countAsFilter?: boolean          false = never counted in the Filters (n) badge
 * }>                                  omit (or []) to hide the Filters button
 *
 * advancedFilterValues?: object      controlled APPLIED values (omit to let the table keep them)
 * onApplyFilters?: (values) => void  Apply clicked and every field is valid
 * onResetFilters?: (values) => void  Reset clicked; values = the defaults
 * applyFiltersOnReset?: boolean      (default false) Reset also applies + closes the drawer
 * onFilterChange?: (key, value, draftValues) => void
 *                                     every edit while the drawer is open — load the next
 *                                     field's options here (e.g. streams for the chosen exam)
 * onFilterDrawerOpen?: (appliedValues) => void   drawer opened (reload dependent lists)
 * filterTitle?, filterSubtitle?      drawer heading (default "Filters" / "Refine your results")
 * The Filters (n) badge counts visible fields whose value differs from their default.
 *
 * filterButtonLabel?: string         default "Filters"
 * hideFilterButton?: boolean         no Filters button in the table toolbar — the drawer is opened from
 *                                     a page-header button instead (useHeaderFilterButton)
 * onAppliedFilterCountChange?: (n) => void   the Filters (n) badge count, for a second button
 * onFilterDrawerOpenChange?: (open) => void  drawer opened / closed (aria-expanded on that button)
 * ref                                { openFilters(), closeFilters() } — open the same drawer
 *                                     from outside, e.g. a page-header Filters button:
 *     const headerFilter = useHeaderFilterButton();
 *     <CustomPageLayout headerActions={headerFilter.button}>
 *       <CustomAdminTable {...headerFilter.tableProps} advancedFilters={…} … />
 *
 * scrollControls?: boolean           (default true) round ◀ ▶ buttons on the
 *                                     table edges when it scrolls sideways
 *                                     (with edge fades), and a ↑ "back to top"
 *                                     button after scrolling down
 */
const CustomAdminTable = forwardRef(function CustomAdminTable({
  columns = [],
  data: dataProp,
  loading = false,
  rowKey,
  onRowClick,

  title,
  search,
  filters,
  toolbarActions,

  actions,
  actionsHeader = "Actions",
  stickyActions = true,

  sort,
  onSortChange,

  pagination,

  emptyTitle = "No records found",
  emptyDescription = "There's nothing to show here yet.",
  emptyIcon,
  emptyState,

  maxHeight = "70vh",
  striped = true,
  hoverable = true,
  dense = false,
  className,
  style,
  id,

  enableSerialNumber = false,
  serialHeader = "#",
  isSerialSkipped,

  enableSelection = false,
  selectedRowKeys,
  onSelectionChange,
  rowClassName,

  stickyHeader = true,
  rowHeight,
  compact = false,

  enableRefresh = false,
  onRefresh,

  loadingText,

  deleteDialog = false,

  exportButton = false,
  onExport,

  advancedFilters,
  advancedFilterValues,
  onApplyFilters,
  onResetFilters,
  onFilterChange,
  onFilterDrawerOpen,
  applyFiltersOnReset = false,
  filterTitle = "Filters",
  filterSubtitle = "Refine your results",
  filterButtonLabel = "Filters",
  hideFilterButton = false,
  onAppliedFilterCountChange,
  onFilterDrawerOpenChange,

  scrollControls = true,
}, ref) {
  // a failed API call often hands back null — treat anything that isn't an array as "no rows"
  const data = Array.isArray(dataProp) ? dataProp : EMPTY_DATA;
  const autoId = useId();
  const instanceId = id || autoId;
  const pageSizeSelectId = `${instanceId}-pageSize`;

  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.hidden),
    [columns],
  );

  const resolvedRowHeight = rowHeight || (compact ? "compact" : "default");
  const heightPreset =
    ROW_HEIGHT_PRESETS[resolvedRowHeight] || ROW_HEIGHT_PRESETS.default;

  const hasToolbar = Boolean(
    title ||
    search ||
    filters ||
    toolbarActions ||
    enableRefresh ||
    exportButton ||
    (Array.isArray(advancedFilters) && advancedFilters.length > 0),
  );
  const hasActionsColumn = typeof actions === "function";
  const hasPrevData = data.length > 0;
  const showSkeleton = loading && !hasPrevData;
  const showOverlay = loading && hasPrevData;
  const showEmpty = !loading && data.length === 0;

  if (process.env.NODE_ENV !== "production") {
    const sortableWithoutHandler =
      visibleColumns.some((c) => c.sortable) && !onSortChange;
    if (sortableWithoutHandler) {
      // eslint-disable-next-line no-console
      console.warn(
        "CustomAdminTable: one or more columns have sortable: true, but no " +
          "onSortChange prop was passed — clicking those headers will do nothing.",
      );
    }
    if (pagination && !pagination.onPageChange) {
      // eslint-disable-next-line no-console
      console.warn(
        "CustomAdminTable: pagination was passed without onPageChange — the " +
          "page buttons will render but won't be able to change pages.",
      );
    }
  }

  const {
    page = 1,
    pageSize = 10,
    total = data.length,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  } = pagination || {};

  const totalPages = Math.max(1, Math.ceil(total / (pageSize || 1)));
  // the parent can keep page 7 after a filter shrinks total — show the last real page's range
  const shownPage = Math.min(page, totalPages);
  const startRow = total === 0 ? 0 : (shownPage - 1) * pageSize + 1;
  const endRow = Math.min(shownPage * pageSize, total);
  const pageList = useMemo(
    () => buildPageList(shownPage, totalPages),
    [shownPage, totalPages],
  );

  // serial per row on this page; Total / sub-total rows get "" and are not counted
  const serialNumbers = useMemo(() => {
    if (!enableSerialNumber) return [];
    const skip = (row, i) => {
      if (isSerialSkipped) return isSerialSkipped(row);
      if (row?._total) return true;
      const cls = typeof rowClassName === "function" ? rowClassName(row, i) : "";
      return /\bcat-row(Grand)?Total\b/.test(cls || "");
    };
    let n = (shownPage - 1) * pageSize; // same page as the footer ("Showing 41–45")
    return data.map((row, i) => (skip(row, i) ? "" : ++n));
  }, [enableSerialNumber, isSerialSkipped, rowClassName, data, shownPage, pageSize]);
  const getSerialNumber = useCallback((index) => serialNumbers[index] ?? "", [serialNumbers]);

  const handleHeaderClick = useCallback(
    (col) => {
      if (!col.sortable || !onSortChange) return;
      onSortChange(col.key);
    },
    [onSortChange],
  );

  const renderSortIcon = (col) => {
    if (!col.sortable) return null;
    if (sort?.key !== col.key)
      return <ChevronsUpDown className="cat-sortIcon cat-sortIcon-idle" />;
    return sort.direction === "asc" ? (
      <ChevronUp className="cat-sortIcon cat-sortIcon-active" />
    ) : (
      <ChevronDown className="cat-sortIcon cat-sortIcon-active" />
    );
  };

  /* ---------- grouped header: columns sharing `group` get one merged cell above them ---------- */

  const headerGroups = useMemo(() => {
    if (!visibleColumns.some((c) => c.group)) return null;
    return visibleColumns.reduce((out, col, i) => {
      const last = out[out.length - 1];
      if (col.group && last?.group === col.group) last.span += 1;
      else out.push(col.group ? { group: col.group, span: 1, first: i } : { col });
      return out;
    }, []);
  }, [visibleColumns]);
  const headerRowSpan = headerGroups ? 2 : undefined;

  const renderColumnHeader = (col, rowSpan) => (
    <th
      key={col.key || col.header}
      scope="col"
      rowSpan={rowSpan}
      className={`${col.sortable ? "cat-thSortable" : ""}${
        col.headerClassName ? ` ${col.headerClassName}` : ""
      }`}
      style={{
        width: col.width,
        minWidth: col.minWidth,
        textAlign: col.align || "left",
      }}
      aria-sort={
        col.sortable
          ? sort?.key === col.key
            ? sort.direction === "asc"
              ? "ascending"
              : "descending"
            : "none"
          : undefined
      }
      onClick={() => handleHeaderClick(col)}
    >
      <span className="cat-thContent">
        {col.header}
        {renderSortIcon(col)}
      </span>
    </th>
  );

  /* ---------- premium search: local echo + 400ms debounce + clear + spinner ---------- */

  const [localSearch, setLocalSearch] = useState(search?.value ?? "");
  const [isDebouncing, setIsDebouncing] = useState(false);
  const debounceRef = useRef(null);

  // Keep in sync if the parent resets/changes search.value externally
  // (e.g. a "Clear filters" button elsewhere on the page).
  useEffect(() => {
    setLocalSearch(search?.value ?? "");
  }, [search?.value]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleSearchInput = useCallback(
    (value) => {
      setLocalSearch(value);
      setIsDebouncing(true);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setIsDebouncing(false);
        search?.onChange?.(value);
      }, search?.debounce ?? DEFAULT_SEARCH_DEBOUNCE_MS);
    },
    [search],
  );

  const handleClearSearch = useCallback(() => {
    clearTimeout(debounceRef.current);
    setIsDebouncing(false);
    setLocalSearch("");
    search?.onChange?.("");
  }, [search]);

  const showSearchSpinner = isDebouncing || (loading && localSearch.length > 0);

  /* ---------- row selection (controlled if selectedRowKeys given, else internal) ---------- */

  const [internalSelectedKeys, setInternalSelectedKeys] = useState([]);
  const isSelectionControlled = Array.isArray(selectedRowKeys);
  const activeSelectedKeys = isSelectionControlled
    ? selectedRowKeys
    : internalSelectedKeys;

  const commitSelection = useCallback(
    (nextKeys) => {
      if (!isSelectionControlled) setInternalSelectedKeys(nextKeys);
      const nextRows = data.filter((row, i) =>
        nextKeys.includes(getRowKey(row, rowKey, i)),
      );
      onSelectionChange?.(nextKeys, nextRows);
    },
    [isSelectionControlled, data, rowKey, onSelectionChange],
  );

  const pageRowKeys = useMemo(
    () => data.map((row, i) => getRowKey(row, rowKey, i)),
    [data, rowKey],
  );
  const allOnPageSelected =
    pageRowKeys.length > 0 &&
    pageRowKeys.every((k) => activeSelectedKeys.includes(k));
  const someOnPageSelected =
    !allOnPageSelected &&
    pageRowKeys.some((k) => activeSelectedKeys.includes(k));

  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current)
      selectAllRef.current.indeterminate = someOnPageSelected;
  }, [someOnPageSelected]);

  const toggleSelectAllOnPage = useCallback(() => {
    if (allOnPageSelected) {
      commitSelection(
        activeSelectedKeys.filter((k) => !pageRowKeys.includes(k)),
      );
    } else {
      commitSelection(
        Array.from(new Set([...activeSelectedKeys, ...pageRowKeys])),
      );
    }
  }, [allOnPageSelected, activeSelectedKeys, pageRowKeys, commitSelection]);

  const toggleRowSelected = useCallback(
    (key) => {
      commitSelection(
        activeSelectedKeys.includes(key)
          ? activeSelectedKeys.filter((k) => k !== key)
          : [...activeSelectedKeys, key],
      );
    },
    [activeSelectedKeys, commitSelection],
  );

  /* ---------- scroll state: sticky-column shadow + ◀ ▶ ↑ buttons ---------- */

  const scrollRef = useRef(null);
  const [scrolledX, setScrolledX] = useState(false);
  const [scrollState, setScrollState] = useState({
    canLeft: false,
    canRight: false,
    showTop: false,
    rightOffset: 0,
  });

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxLeft = el.scrollWidth - el.clientWidth;
    // keep the ▶ button clear of the sticky Actions column
    const stickyCol = stickyActions ? el.querySelector("thead th.cat-actionsCol") : null;
    const next = {
      canLeft: el.scrollLeft > 2,
      canRight: maxLeft - el.scrollLeft > 2,
      showTop: el.scrollTop > 240,
      rightOffset: stickyCol ? stickyCol.offsetWidth : 0,
    };
    setScrolledX(next.canLeft);
    setScrollState((prev) =>
      prev.canLeft === next.canLeft &&
      prev.canRight === next.canRight &&
      prev.showTop === next.showTop &&
      prev.rightOffset === next.rightOffset
        ? prev
        : next,
    );
  }, [stickyActions]);

  const handleTableScroll = updateScrollState;

  // Re-measure when content or size changes (new data, columns, window resize)
  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return undefined;
    window.addEventListener("resize", updateScrollState);
    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateScrollState);
      observer.observe(el);
      const table = el.querySelector("table");
      if (table) observer.observe(table);
    }
    return () => {
      window.removeEventListener("resize", updateScrollState);
      observer?.disconnect();
    };
  }, [updateScrollState, data, visibleColumns, loading]);

  const scrollByPage = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = Math.max(200, el.clientWidth * 0.6) * direction;
    if (typeof el.scrollBy === "function") el.scrollBy({ left: amount, behavior: "smooth" });
    else el.scrollLeft += amount;
  };

  const scrollToTop = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (typeof el.scrollTo === "function") el.scrollTo({ top: 0, behavior: "smooth" });
    else el.scrollTop = 0;
  };

  /* ---------- delete-confirmation dialog (only used when deleteDialog=true) ---------- */

  const [confirmDialog, setConfirmDialog] = useState(null); // { message, onConfirm } | null

  const requestConfirm = useCallback((message, onConfirm) => {
    setConfirmDialog({ message, onConfirm });
  }, []);

  const closeConfirmDialog = () => setConfirmDialog(null);

  const handleConfirmProceed = () => {
    confirmDialog?.onConfirm?.();
    setConfirmDialog(null);
  };

  const confirmContextValue = useMemo(
    () => (deleteDialog ? { requestConfirm } : null),
    [deleteDialog, requestConfirm],
  );

  /* ---------- advanced filter drawer ----------
     Draft state is separate from "applied" state on purpose: editing a
     field in the drawer shouldn't notify the parent (or re-run any
     search) until Search/Apply is actually clicked. Reopening the
     drawer re-seeds the draft from the last applied values, so closing
     without applying never leaves stray half-edited state behind. */

  const hasAdvancedFilters =
    Array.isArray(advancedFilters) && advancedFilters.length > 0;

  // Per-field settings may be a plain value or a function of the current values
  const resolveFieldProp = (prop, values) =>
    typeof prop === "function" ? prop(values) : prop;

  // Initial + Reset values: field.defaultValue, else "" (false for toggles)
  const defaultFilterValues = useMemo(() => {
    const obj = {};
    (advancedFilters || []).forEach((f) => {
      const fallback = f.type === "toggle" ? false : "";
      const def =
        typeof f.defaultValue === "function" ? f.defaultValue() : f.defaultValue;
      obj[f.key] = def === undefined ? fallback : def;
    });
    return obj;
  }, [advancedFilters]);

  const isFiltersControlled = Boolean(advancedFilterValues);
  const [internalAppliedFilters, setInternalAppliedFilters] =
    useState(defaultFilterValues);
  const appliedFilters = isFiltersControlled
    ? { ...defaultFilterValues, ...advancedFilterValues }
    : internalAppliedFilters;

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(appliedFilters);
  const [filterSubmitAttempted, setFilterSubmitAttempted] = useState(false);

  // whichever button opened the drawer (the table's or a page-header one) gets focus back
  const drawerOpenerRef = useRef(null);
  const openFilterDrawer = () => {
    // no fields yet (e.g. a header button clicked before they load): don't leave a pending "open"
    if (!hasAdvancedFilters) return;
    drawerOpenerRef.current = document.activeElement;
    setDraftFilters(appliedFilters); // re-seed from the last APPLIED values, not any stale draft
    setFilterSubmitAttempted(false);
    setFilterDrawerOpen(true);
    onFilterDrawerOpen?.(appliedFilters); // lets the parent reload dependent option lists
  };
  const closeFilterDrawer = () => setFilterDrawerOpen(false);

  // open / close the same drawer from outside (e.g. a page-header Filters button)
  useImperativeHandle(ref, () => ({ openFilters: openFilterDrawer, closeFilters: closeFilterDrawer }));
  useEffect(() => {
    onFilterDrawerOpenChange?.(filterDrawerOpen); // lets a header button mirror aria-expanded
  }, [filterDrawerOpen, onFilterDrawerOpenChange]);

  // The open drawer behaves like a modal: focus moves in, Tab stays in, Esc closes,
  // and focus goes back to the Filters button. (Closed, it is `inert` — see the JSX.)
  const drawerRef = useRef(null);
  const filterBtnRef = useRef(null);
  const drawerWasOpenRef = useRef(false);
  useEffect(() => {
    if (!filterDrawerOpen) {
      if (drawerWasOpenRef.current) {
        const opener = drawerOpenerRef.current;
        (opener?.isConnected && opener !== document.body ? opener : filterBtnRef.current)?.focus();
      }
      drawerWasOpenRef.current = false;
      return undefined;
    }
    drawerWasOpenRef.current = true;
    const focusables = () => [...(drawerRef.current?.querySelectorAll(DRAWER_FOCUSABLE) || [])];
    const focusTimer = setTimeout(() => {
      const body = drawerRef.current?.querySelector(".cat-drawerBody");
      (body?.querySelector(DRAWER_FOCUSABLE) || focusables()[0])?.focus();
    }, 0);
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        // an open dropdown menu inside the drawer takes the first Esc
        if (document.querySelector(".cdd-menu-portal, .msd-panel")) return;
        setFilterDrawerOpen(false);
      } else if (e.key === "Tab") {
        const items = focusables();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [filterDrawerOpen]);

  // Editing a field: update the draft, clear dependent fields (`clears`),
  // and tell the parent so it can load the next field's options.
  // (side effect kept OUT of the state updater, so StrictMode can't call the parent twice)
  const handleDraftFieldChange = (key, value) => {
    const field = (advancedFilters || []).find((f) => f.key === key);
    const next = { ...draftFilters, [key]: value };
    (field?.clears || []).forEach((k) => {
      next[k] = defaultFilterValues[k] ?? "";
    });
    setDraftFilters(next);
    onFilterChange?.(key, value, next);
  };

  const isEmptyFilterValue = (v) =>
    v === "" ||
    v === null ||
    v === undefined ||
    v === false ||
    (Array.isArray(v) && v.length === 0) ||
    (typeof v === "string" && v.trim() === "");

  // Errors for the visible fields: field.validate(), plus "X is required"
  // once the user has tried to apply.
  const filterErrors = useMemo(() => {
    const errs = {};
    (advancedFilters || []).forEach((f) => {
      if (resolveFieldProp(f.hidden, draftFilters)) return;
      const value = draftFilters[f.key];
      const custom = f.validate ? f.validate(value, draftFilters) : "";
      if (custom) errs[f.key] = custom;
      else if (f.isRequired && filterSubmitAttempted && isEmptyFilterValue(value)) {
        errs[f.key] = `${f.label} is required`;
      }
    });
    return errs;
  }, [advancedFilters, draftFilters, filterSubmitAttempted]);

  const hasRequiredMissing = (advancedFilters || []).some(
    (f) =>
      f.isRequired &&
      !resolveFieldProp(f.hidden, draftFilters) &&
      isEmptyFilterValue(draftFilters[f.key]),
  );

  const applyFilterValues = (values) => {
    if (!isFiltersControlled) setInternalAppliedFilters(values);
    onApplyFilters?.(values);
    setFilterDrawerOpen(false);
  };

  const handleApplyFilters = () => {
    setFilterSubmitAttempted(true);
    if (Object.keys(filterErrors).length > 0 || hasRequiredMissing) return;
    applyFilterValues(draftFilters);
  };

  const handleResetFilters = () => {
    setDraftFilters(defaultFilterValues);
    setFilterSubmitAttempted(false);
    onResetFilters?.(defaultFilterValues);
    if (applyFiltersOnReset) applyFilterValues(defaultFilterValues);
  };

  // Count of visible fields that differ from their default value
  const countChangedFilters = (values) =>
    (advancedFilters || []).filter((f) => {
      if (f.countAsFilter === false) return false;
      if (resolveFieldProp(f.hidden, values)) return false;
      const v = values[f.key];
      if (isEmptyFilterValue(v)) return false;
      return v !== defaultFilterValues[f.key];
    }).length;

  // Toolbar badge = applied filters; drawer header pill = what's selected right now
  const appliedFilterCount = useMemo(
    () => countChangedFilters(appliedFilters),
    [advancedFilters, appliedFilters, defaultFilterValues],
  );
  useEffect(() => {
    onAppliedFilterCountChange?.(appliedFilterCount);
  }, [appliedFilterCount, onAppliedFilterCountChange]);
  const draftFilterCount = useMemo(
    () => countChangedFilters(draftFilters),
    [advancedFilters, draftFilters, defaultFilterValues],
  );

  /* ---------- one drawer field, built from the shared Custom* inputs ---------- */
  const optionValueOf = (opt) =>
    typeof opt === "string" || typeof opt === "number"
      ? opt
      : (opt?.value ?? opt?._id ?? opt?.id);

  const renderFilterField = (field) => {
    const values = draftFilters;
    if (resolveFieldProp(field.hidden, values)) return null;

    const fieldId = `${instanceId}-filter-${field.key}`;
    const value =
      values[field.key] ?? (field.type === "toggle" ? false : field.type === "multiSelect" ? [] : "");
    const options = resolveFieldProp(field.options, values) || [];
    const common = {
      id: fieldId,
      label: field.label,
      isRequired: field.isRequired,
      hint: resolveFieldProp(field.hint, values),
      error: filterErrors[field.key],
      disabled: Boolean(resolveFieldProp(field.disabled, values)),
    };
    const onChange = (v) => handleDraftFieldChange(field.key, v);

    let control;
    switch (field.type) {
      case "toggle":
        control = <CustomToggleSwitch {...common} checked={Boolean(value)} onChange={onChange} />;
        break;

      case "radio": {
        const selected = options.find((o) => optionValueOf(o) === value) ?? "";
        control = (
          <CustomRadioGroup
            {...common}
            options={options}
            value={selected}
            onChange={(item) => onChange(optionValueOf(item))}
          />
        );
        break;
      }

      // value = the whole selected item (object), searchable
      case "dropdown":
        control = (
          <CustomDropdowns
            {...common}
            options={options}
            value={value}
            onChange={onChange}
            placeholder={field.placeholder}
            search={field.search ?? true}
            clearable={field.clearable ?? true}
            getOptionLabel={field.getOptionLabel}
            getOptionKey={field.getOptionKey}
          />
        );
        break;

      // legacy: value = the option's id as a string (unchanged output)
      case "select": {
        const selected =
          options.find((o) => String(optionValueOf(o)) === String(value)) ?? "";
        control = (
          <CustomDropdowns
            {...common}
            options={options}
            value={selected}
            onChange={(item) => onChange(item === "" ? "" : String(optionValueOf(item)))}
            placeholder={field.placeholder || `All ${field.label}`}
            search={field.search ?? options.length > 8}
          />
        );
        break;
      }

      // value = array of the selected options (objects or strings)
      case "multiSelect":
        control = (
          <CustomFormField label={field.label} isRequired={field.isRequired} htmlFor={fieldId} hint={common.hint} error={common.error} noMargin>
            <MultiSelectDropdown
              options={options}
              value={Array.isArray(value) ? value : []}
              onChange={onChange}
              placeholder={field.placeholder || `Select ${field.label || ""}`}
              disabled={common.disabled}
            />
          </CustomFormField>
        );
        break;

      case "date":
        control = (
          <CustomDatePicker
            {...common}
            value={value}
            onChange={onChange}
            minDate={resolveFieldProp(field.minDate, values)}
            maxDate={resolveFieldProp(field.maxDate, values)}
            clearable={field.clearable ?? !field.isRequired}
            noMargin
          />
        );
        break;

      default:
        control = (
          <CustomInputField
            {...common}
            type={field.type === "number" ? "number" : "text"}
            value={value}
            onChange={onChange}
            placeholder={field.placeholder}
            noMargin
          />
        );
    }

    return (
      <div
        key={field.key}
        className={`cat-drawerField${field.half ? " cat-drawerField-half" : ""}`}
      >
        {control}
      </div>
    );
  };

  const skeletonRows = Array.from({ length: Math.min(pageSize || 6, 8) });

  return (
    <CatConfirmContext.Provider value={confirmContextValue}>
      <div
        className={`cat-wrap${dense ? " cat-dense" : ""}${scrolledX ? " cat-scrolledX" : ""}${
          !stickyHeader ? " cat-headerStatic" : ""
        }${className ? ` ${className}` : ""}`}
        style={{
          "--cat-max-height": maxHeight,
          "--cat-header-h": `${heightPreset.header}px`,
          "--cat-row-h": `${heightPreset.row}px`,
          ...style,
        }}
        data-testid={`cat-table-${instanceId}`}
      >
        {hasToolbar && (
          <div className="cat-toolbar">
            <div className="cat-toolbarLeft">
              {title && <h3 className="cat-title">{title}</h3>}
            </div>

            <div className="cat-toolbarCenter">
              {search && (
                <div className="cat-search">
                  <Search className="cat-searchIcon" aria-hidden="true" />
                  <input
                    type="text"
                    className="cat-searchInput"
                    placeholder={search.placeholder || "Search..."}
                    value={localSearch}
                    onChange={(e) => handleSearchInput(e.target.value)}
                  />
                  {showSearchSpinner ? (
                    <Loader2 className="cat-searchSpinner" aria-hidden="true" />
                  ) : (
                    localSearch && (
                      <button
                        type="button"
                        className="cat-searchClear"
                        onClick={handleClearSearch}
                        aria-label="Clear search"
                      >
                        <X size={13} />
                      </button>
                    )
                  )}
                </div>
              )}
              {filters && <div className="cat-filters">{filters}</div>}
              {loadingText && (showSkeleton || showOverlay) && (
                <span className="cat-loadingLabel">
                  <Loader2
                    className="cat-loadingLabelSpinner"
                    aria-hidden="true"
                  />
                  {loadingText}
                </span>
              )}
            </div>

            <div className="cat-toolbarRight">
              {enableRefresh && (
                <button
                  type="button"
                  className="cat-refreshBtn"
                  onClick={onRefresh}
                  disabled={loading}
                  aria-label="Refresh"
                  data-tooltip="Refresh"
                >
                  <RotateCw
                    size={15}
                    className={loading ? "cat-spinning" : ""}
                  />
                </button>
              )}
              {exportButton && (
                <button
                  type="button"
                  className="cat-exportBtn"
                  onClick={onExport}
                  title="Download this table as an Excel file"
                >
                  <Download size={14} />
                  Export
                </button>
              )}
              {hasAdvancedFilters && !hideFilterButton && (
                <CustomFilterButton
                  ref={filterBtnRef}
                  count={appliedFilterCount}
                  label={filterButtonLabel}
                  expanded={filterDrawerOpen}
                  onClick={openFilterDrawer}
                  standalone={false}
                />
              )}
              {toolbarActions && (
                <div className="cat-toolbarActions">{toolbarActions}</div>
              )}
            </div>
          </div>
        )}

        <div
          className={`cat-scrollWrap${scrollControls && scrollState.canLeft ? " cat-canLeft" : ""}${
            scrollControls && scrollState.canRight ? " cat-canRight" : ""
          }`}
          style={{ "--cat-sticky-right": `${scrollState.rightOffset}px` }}
        >
        {scrollControls && scrollState.canLeft && (
          <button
            type="button"
            className="cat-scrollBtn cat-scrollBtn-left"
            onClick={() => scrollByPage(-1)}
            aria-label="Scroll left"
            title="Scroll left"
          >
            <ChevronLeft size={18} />
          </button>
        )}
        {scrollControls && scrollState.canRight && (
          <button
            type="button"
            className="cat-scrollBtn cat-scrollBtn-right"
            onClick={() => scrollByPage(1)}
            aria-label="Scroll right"
            title="Scroll right"
          >
            <ChevronRight size={18} />
          </button>
        )}
        {scrollControls && scrollState.showTop && (
          <button
            type="button"
            className="cat-scrollBtn cat-scrollBtn-top"
            onClick={scrollToTop}
            aria-label="Back to top"
            title="Back to top"
          >
            <ArrowUp size={16} />
          </button>
        )}
        <div
          className="cat-scroll"
          ref={scrollRef}
          onScroll={handleTableScroll}
        >
          <table className="cat-table">
            <thead>
              <tr>
                {enableSelection && (
                  <th scope="col" className="cat-selectCol" rowSpan={headerRowSpan}>
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allOnPageSelected}
                      onChange={toggleSelectAllOnPage}
                      aria-label="Select all rows on this page"
                    />
                  </th>
                )}
                {enableSerialNumber && (
                  <th scope="col" className="cat-serialCol" rowSpan={headerRowSpan}>
                    {serialHeader}
                  </th>
                )}
                {headerGroups
                  ? headerGroups.map((g) =>
                      g.group ? (
                        <th key={`group-${g.first}`} scope="colgroup" colSpan={g.span} className="cat-thGroup">
                          {g.group}
                        </th>
                      ) : (
                        renderColumnHeader(g.col, 2)
                      ),
                    )
                  : visibleColumns.map((col) => renderColumnHeader(col))}
                {hasActionsColumn && (
                  <th
                    scope="col"
                    className={`cat-actionsCol${stickyActions ? " cat-sticky" : ""}`}
                    style={{ textAlign: "right" }}
                    rowSpan={headerRowSpan}
                  >
                    {actionsHeader}
                  </th>
                )}
              </tr>
              {/* second header row: the columns under each group */}
              {headerGroups && (
                <tr className="cat-thSubRow">
                  {visibleColumns.filter((col) => col.group).map((col) => renderColumnHeader(col))}
                </tr>
              )}
            </thead>

            <tbody>
              {showSkeleton &&
                skeletonRows.map((_, i) => (
                  <tr key={`skeleton-${i}`} className="cat-skeletonRow">
                    {enableSelection && (
                      <td>
                        <span
                          className="cat-skeletonBar"
                          style={{ width: 16 }}
                        />
                      </td>
                    )}
                    {enableSerialNumber && (
                      <td>
                        <span
                          className="cat-skeletonBar"
                          style={{ width: 20 }}
                        />
                      </td>
                    )}
                    {visibleColumns.map((col) => (
                      <td key={col.key || col.header}>
                        <span className="cat-skeletonBar" />
                      </td>
                    ))}
                    {hasActionsColumn && (
                      <td className={stickyActions ? "cat-sticky" : ""}>
                        <span
                          className="cat-skeletonBar"
                          style={{ width: "60%", marginLeft: "auto" }}
                        />
                      </td>
                    )}
                  </tr>
                ))}

              {!showSkeleton &&
                data.map((row, rowIndex) => {
                  const key = getRowKey(row, rowKey, rowIndex);
                  const isSelected = activeSelectedKeys.includes(key);
                  return (
                    <tr
                      key={key}
                      className={`${onRowClick ? "cat-rowClickable" : ""}${
                        isSelected ? " cat-rowSelected" : ""
                      }${rowClassName ? ` ${rowClassName(row, rowIndex) || ""}` : ""}`}
                      onClick={
                        onRowClick ? () => onRowClick(row, rowIndex) : undefined
                      }
                    >
                      {enableSelection && (
                        <td
                          className="cat-selectCol"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRowSelected(key)}
                            aria-label={`Select row ${rowIndex + 1}`}
                          />
                        </td>
                      )}
                      {enableSerialNumber && (
                        <td className="cat-serialCol">
                          {getSerialNumber(rowIndex)}
                        </td>
                      )}
                      {visibleColumns.map((col) => {
                        const value = getCellValue(row, col);
                        const content = col.render
                          ? col.render(value, row, rowIndex)
                          : value === null ||
                              value === undefined ||
                              value === ""
                            ? "—"
                            : value;
                        return (
                          <td
                            key={col.key || col.header}
                            className={`${col.wrap ? "cat-tdWrap" : ""}${
                              col.className ? ` ${col.className}` : ""
                            }`}
                            style={{ textAlign: col.align || "left" }}
                          >
                            {content}
                          </td>
                        );
                      })}
                      {hasActionsColumn && (
                        <td
                          className={`cat-actionsCell${stickyActions ? " cat-sticky" : ""}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="cat-actionsRow">
                            {actions(row, rowIndex)}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>

          {showEmpty &&
            (emptyState || (
              <div className="cat-empty">
                <div className="cat-emptyIcon">
                  {emptyIcon || <Inbox size={26} />}
                </div>
                <div className="cat-emptyTitle">{emptyTitle}</div>
                {emptyDescription && (
                  <div className="cat-emptyDesc">{emptyDescription}</div>
                )}
              </div>
            ))}

          {showOverlay && (
            <div className="cat-loadingOverlay">
              <Loader2 className="cat-loadingSpinner" />
              {loadingText && (
                <span className="cat-loadingOverlayText">{loadingText}</span>
              )}
            </div>
          )}
        </div>
        </div>

        {pagination && (
          <div className="cat-footer">
            <div className="cat-footerInfo">
              {total === 0
                ? "0 records"
                : `Showing ${startRow}–${endRow} of ${total} records`}
            </div>

            <div className="cat-footerControls">
              <div className="cat-pageSize">
                <label htmlFor={pageSizeSelectId}>Rows</label>
                <select
                  id={pageSizeSelectId}
                  value={pageSize}
                  onChange={(e) => {
                    if (
                      !onPageSizeChange &&
                      process.env.NODE_ENV !== "production"
                    ) {
                      // eslint-disable-next-line no-console
                      console.warn(
                        "CustomAdminTable: pagination.onPageSizeChange wasn't passed, so " +
                          "changing rows-per-page won't do anything on this page.",
                      );
                      return;
                    }
                    onPageSizeChange?.(Number(e.target.value));
                  }}
                >
                  {pageSizeOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cat-pager">
                <button
                  type="button"
                  className="cat-pageBtn"
                  disabled={shownPage <= 1}
                  onClick={() => onPageChange?.(1)}
                  aria-label="First page"
                  data-tooltip="First"
                >
                  <ChevronsLeft size={15} />
                </button>
                <button
                  type="button"
                  className="cat-pageBtn"
                  disabled={shownPage <= 1}
                  onClick={() => onPageChange?.(shownPage - 1)}
                  aria-label="Previous page"
                  data-tooltip="Previous"
                >
                  <ChevronLeft size={15} />
                </button>

                {pageList.map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="cat-pageEllipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      className={`cat-pageBtn${p === shownPage ? " cat-pageBtn-active" : ""}`}
                      onClick={() => p !== page && onPageChange?.(p)} // already on it → no refetch
                      aria-current={p === shownPage ? "page" : undefined}
                    >
                      {p}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="cat-pageBtn"
                  disabled={shownPage >= totalPages}
                  onClick={() => onPageChange?.(shownPage + 1)}
                  aria-label="Next page"
                  data-tooltip="Next"
                >
                  <ChevronRight size={15} />
                </button>
                <button
                  type="button"
                  className="cat-pageBtn"
                  disabled={shownPage >= totalPages}
                  onClick={() => onPageChange?.(totalPages)}
                  aria-label="Last page"
                  data-tooltip="Last"
                >
                  <ChevronsRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {confirmDialog && (
        <div className="cat-confirmOverlay" onClick={closeConfirmDialog}>
          <div className="cat-confirmCard" onClick={(e) => e.stopPropagation()}>
            <div className="cat-confirmIcon">
              <AlertTriangle size={20} />
            </div>
            <div className="cat-confirmBody">
              <h4 className="cat-confirmTitle">Are you sure?</h4>
              <p className="cat-confirmMessage">{confirmDialog.message}</p>
            </div>
            <div className="cat-confirmActions">
              <button
                type="button"
                className="cat-confirmBtn cat-confirmBtn-cancel"
                onClick={closeConfirmDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cat-confirmBtn cat-confirmBtn-danger"
                onClick={handleConfirmProceed}
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {hasAdvancedFilters && (
        <div
          className={`cat-drawerOverlay${filterDrawerOpen ? " cat-drawerOverlay-open" : ""}`}
          onClick={closeFilterDrawer}
          aria-hidden={!filterDrawerOpen}
        >
          <div
            ref={drawerRef}
            className={`cat-drawer${filterDrawerOpen ? " cat-drawer-open" : ""}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={filterButtonLabel}
            aria-modal={filterDrawerOpen ? "true" : undefined}
            inert={filterDrawerOpen ? undefined : ""}
          >
            <div className="cat-drawerHeader">
              <div className="cat-drawerTitleWrap">
                <div className="cat-drawerTitleIcon" aria-hidden="true">
                  <SlidersHorizontal size={18} strokeWidth={2.2} />
                </div>

                <div className="cat-drawerTitleText">
                  <div className="cat-drawerTitleRow">
                    <h4>{filterTitle}</h4>
                    {draftFilterCount > 0 && (
                      <span className="cat-drawerCount">{draftFilterCount} selected</span>
                    )}
                  </div>
                  {filterSubtitle && (
                    <p className="cat-drawerSubtitle mb-0">{filterSubtitle}</p>
                  )}
                </div>
              </div>

              <button
                className="cat-drawerClose"
                type="button"
                onClick={closeFilterDrawer}
                aria-label="Close filters"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Only this middle section scrolls — same fixed-header/
              scrolling-body/fixed-footer pattern as the table itself. */}
            <div className="cat-drawerBody">
              {advancedFilters.map((field) => renderFilterField(field))}
            </div>

            <div className="cat-drawerFooter">
              <CustomButton
                variant="ghost"
                icon={<ListRestart size={14} />}
                onClick={handleResetFilters}
              >
                Reset
              </CustomButton>
              <CustomButton
                icon={<Filter size={14} />}
                onClick={handleApplyFilters}
                disabled={Object.keys(filterErrors).length > 0}
              >
                Apply
              </CustomButton>
            </div>
          </div>
        </div>
      )}
    </CatConfirmContext.Provider>
  );
});

/**
 * A page-header Filters button that opens the table's own filter drawer (same fields, same
 * Apply / Reset) and shows the same applied-filter count.
 *   const headerFilter = useHeaderFilterButton();
 *   <CustomPageLayout title="…" headerActions={headerFilter.button}>
 *     <CustomAdminTable {...headerFilter.tableProps} advancedFilters={…} … />
 */
export const useHeaderFilterButton = (label = "Filters") => {
  const tableRef = useRef(null);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  return {
    tableProps: { ref: tableRef, onAppliedFilterCountChange: setCount, onFilterDrawerOpenChange: setOpen },
    button: <CustomFilterButton count={count} label={label} expanded={open} onClick={() => tableRef.current?.openFilters()} />,
  };
};

export default React.memo(CustomAdminTable);
